import { get } from 'svelte/store';

import type { SettingsConfig } from 'src/types/Settings';

import { user_data, user_settings } from 'src/stores/account/user';
import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

// Legacy components — Home settings (per FigJam Multi-home settings board)
import GetKey from 'src/components/Settings/AccountSettings/Security/GetKey.svelte';
import GrantInstallerAccess from 'src/components/Settings/AccountSettings/GrantInstallerAccess.svelte';
import AppBackground from 'src/components/Settings/AppSettings/AppBackground.svelte';
import HomeInfo from 'src/components/Settings/HomeManagement/HomeInfo.svelte';
import LockPin from 'src/components/Settings/AppSettings/LockPin.svelte';
import MenuItemsOrder from 'src/components/Settings/AppSettings/MenuItemsOrder.svelte';
import SelectImage from 'src/components/Settings/Tools/SelectImage.svelte';
import StatisticsSettings from 'src/components/Settings/AppSettings/StatisticsSettings.svelte';
import TimeZone from 'src/components/Settings/AccountSettings/Localization/TimeZone.svelte';
import MyHomes from 'src/components/Settings/HomeManagement/MyHomes.svelte';
import ShareHomeAccess from 'src/components/Settings/HomeManagement/ShareHomeAccess.svelte';

// V2 components — Home settings (new layout)
import AppBackgroundV2 from 'src/components/Settings/AppSettings/AppBackgroundV2.svelte';
import TimeZoneV2 from 'src/components/Settings/AccountSettings/Localization/TimeZoneV2.svelte';
import GetKeyV2 from 'src/components/Settings/AccountSettings/Security/GetKeyV2.svelte';
import GrantInstallerAccessV2 from 'src/components/Settings/AccountSettings/GrantInstallerAccessV2.svelte';
import LockPinV2 from 'src/components/Settings/AppSettings/LockPinV2.svelte';
import MenuItemsOrderV2 from 'src/components/Settings/AppSettings/MenuItemsOrderV2.svelte';
import SelectImageV2 from 'src/components/Settings/Tools/SelectImageV2.svelte';
import StatisticsSettingsV2 from 'src/components/Settings/AppSettings/StatisticsSettingsV2.svelte';
import MyHomesV2 from 'src/components/Settings/HomeManagement/MyHomesV2.svelte';
import ShareHomeAccessV2 from 'src/components/Settings/HomeManagement/ShareHomeAccessV2.svelte';
import { isSubAccount } from 'src/helpers/user_helpers';

const isInstaller = (): boolean => Boolean(get(user_data)?.installer);
const isOwnedHomeActive = (): boolean => !isActiveHomeShared();

export const homeSettingsConfig: SettingsConfig = {
	legacy: [
		{
			id: 'homeInfo',
			title: 'label_home_info',
			titleDefault: 'Home info',
			component: HomeInfo,
			lineContainer: true,
		},
		{
			id: 'myHomes',
			title: 'settings_my_homes',
			titleDefault: 'My Homes',
			subtitle: 'settings_my_homes_description',
			subtitleDefault: 'View and manage your homes.',
			component: MyHomes,
			visible: () => !isInstaller(),
		},
		{
			id: 'shareHomeAccess',
			title: 'settings_share_home_access',
			titleDefault: 'Share home access',
			subtitle: 'settings_share_home_access_description',
			subtitleDefault: 'Invite people to access this home.',
			component: ShareHomeAccess,
			visible: () => !isInstaller(),
		},
		{
			id: 'appBackground',
			title: 'title_change_app_background',
			titleDefault: 'Change App Background',
			component: AppBackground,
		},
		{
			id: 'location',
			title: 'label_location',
			titleDefault: 'Location',
			groups: [
				{
					id: 'timeZone',
					title: 'collapse_title_time_zone',
					titleDefault: 'Time zone',
					component: TimeZone,
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_privacy_and_security',
			titleDefault: 'Privacy and Security',
			groups: [
				{
					id: 'lockPin',
					title: 'lock_pin',
					titleDefault: 'Lock pin',
					component: LockPin,
				},
			],
		},
		{
			id: 'customizations',
			title: 'label_customizations',
			titleDefault: 'Customizations',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'managePictures',
					title: 'title_menu_items_manage_pictures',
					titleDefault: 'Manage Pictures',
					component: SelectImage,
				},
			],
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and permissions',
			visible: () => !isInstaller() && !isSubAccount(),
			groups: [
				{
					id: 'installerAccess',
					title: 'collapse_title_grant_installer_access',
					titleDefault: 'Grant installer access',
					component: GrantInstallerAccess,
				},
				{
					id: 'cloudKey',
					title: 'cloud_key',
					titleDefault: 'Cloud Key',
					component: GetKey,
				},
			],
		},
		{
			id: 'personalization',
			title: 'label_personalization',
			titleDefault: 'Personalization',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'menuOrder',
					title: 'collapse_title_menu_order',
					titleDefault: 'Menu order',
					component: MenuItemsOrder,
				},
				{
					id: 'statistics',
					title: 'collapse_title_statistics',
					titleDefault: 'Statistics',
					component: StatisticsSettings,
				},
			],
		},
	],
	v2: [
		{
			id: 'homeInfo',
			title: 'label_home_info',
			titleDefault: 'Home info',
			component: HomeInfo,
			lineContainer: true,
		},
		{
			id: 'myHomes',
			title: 'settings_my_homes',
			titleDefault: 'My Homes',
			subtitle: 'settings_my_homes_description',
			subtitleDefault: 'View and manage your homes.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'myHomesGroup',
					components: [{ id: 'myHomes', component: MyHomesV2 }],
				},
			],
		},
		{
			id: 'shareHomeAccess',
			title: 'settings_share_home_access',
			titleDefault: 'Share home access',
			subtitle: 'settings_share_home_access_description',
			subtitleDefault: 'Invite people to access this home.',
			visible: () => !isInstaller(),
			groups: [
				{
					id: 'shareHomeAccessGroup',
					components: [{ id: 'shareHomeAccess', component: ShareHomeAccessV2 }],
				},
			],
		},
		{
			id: 'appBackground',
			title: 'title_change_app_background',
			titleDefault: 'Change App Background',
			subtitle: 'app_settings_background_description',
			subtitleDefault: 'Choose your app background and accent color.',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'appBackgroundGroup',
					components: [{ id: 'appBackground', component: AppBackgroundV2 }],
				},
			],
		},
		{
			id: 'location',
			title: 'label_location',
			titleDefault: 'Location',
			subtitle: 'settings_location_subtitle_new',
			subtitleDefault: 'Manage Location settings of your home',
			groups: [
				{
					id: 'locationGroup',
					components: [{ id: 'timeZone', component: TimeZoneV2 }],
				},
			],
		},
		{
			id: 'privacySecurity',
			title: 'label_privacy_and_security',
			titleDefault: 'Privacy and Security',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'lockPinGroup',
					title: 'lock_pin',
					titleDefault: 'Lock pin',
					components: [{ id: 'lockPin', component: LockPinV2 }],
				},
			],
		},
		{
			id: 'customizations',
			title: 'label_customizations',
			titleDefault: 'Customizations',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'managePicturesGroup',
					title: 'title_menu_items_manage_pictures',
					titleDefault: 'Manage Pictures',
					components: [{ id: 'managePictures', component: SelectImageV2 }],
				},
			],
		},
		{
			id: 'accessPermissions',
			title: 'label_title_access_permissions',
			titleDefault: 'Access and Permissions',
			subtitle: 'settings_access_permissions_subtitle_new',
			subtitleDefault: 'Control who can access your home and devices.',
			visible: () => !isInstaller() && !isSubAccount(),
			groups: [
				{
					id: 'installerAccessGroup',
					title: 'collapse_title_grant_installer_access',
					titleDefault: 'Grant installer access',
					components: [{ id: 'installerAccess', component: GrantInstallerAccessV2 }],
				},
				{
					id: 'cloudKeyGroup',
					title: 'cloud_key',
					titleDefault: 'Cloud Key',
					components: [{ id: 'authKey', component: GetKeyV2 }],
				},
			],
		},
		{
			id: 'personalization',
			title: 'label_personalization',
			titleDefault: 'Personalization',
			visible: isOwnedHomeActive,
			groups: [
				{
					id: 'menuOrderGroup',
					title: 'collapse_title_menu_order',
					titleDefault: 'Menu order',
					components: [{ id: 'menuOrder', component: MenuItemsOrderV2 }],
				},
				{
					id: 'statisticsGroup',
					title: 'collapse_title_statistics',
					titleDefault: 'Statistics',
					components: [{ id: 'statistics', component: StatisticsSettingsV2 }],
				},
			],
		},
	],
};
